import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import {
  createRazorpayOrder,
  openRazorpayCheckout,
  verifyRazorpayPayment,
  createShiprocketOrder,
} from "../lib/api";
import "./Checkout.css";

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);

export default function Checkout() {
  const { cart, clearCart } = useCartWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [savedProfile, setSavedProfile] = useState(null);
  const [useSavedAddress, setUseSavedAddress] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: user?.email || "",
    phone: "",
    house: "",
    street: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
    addressType: "Home",
  });
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  // ─── Pincode auto-lookup ───
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState(""); // "success" | "error" | ""
  const [shippingError, setShippingError] = useState("");

  // Compose full address from parts
  const composeAddress = useCallback((fd) => {
    return [fd.house, fd.street, fd.landmark].filter(Boolean).join(", ");
  }, []);

  // Pincode lookup via India Post API
  const lookupPincode = useCallback(async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    setPincodeLoading(true);
    setPincodeStatus("");
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const json = await res.json();
      if (json?.[0]?.Status === "Success" && json[0].PostOffice?.length > 0) {
        const po = json[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          city: po.District || prev.city,
          state: po.State || prev.state,
        }));
        setPincodeStatus("success");
        setErrors((prev) => ({ ...prev, pincode: "", city: "", state: "" }));
      } else {
        setPincodeStatus("error");
      }
    } catch {
      setPincodeStatus("error");
    } finally {
      setPincodeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      supabase.from("users").select("*").eq("id", user.id).single().then(({ data }) => {
        if (data && (data.address || data.phone)) {
          setSavedProfile(data);
          setUseSavedAddress(true);
          const fullName = data.full_name || `${data.first_name || ""} ${data.last_name || ""}`.trim();
          setFormData((prev) => ({
            ...prev,
            name: fullName || prev.name,
            phone: data.phone || prev.phone,
            house: data.house || prev.house,
            street: data.street || data.address || prev.street,
            landmark: data.landmark || prev.landmark,
            city: data.city || prev.city,
            state: data.state || prev.state,
            pincode: data.pincode || prev.pincode,
          }));
        }
      });
    }
  }, [user]);

  const handleToggleAddress = (e) => {
    const checked = e.target.checked;
    setUseSavedAddress(checked);
    if (checked && savedProfile) {
      const fullName = savedProfile.full_name || `${savedProfile.first_name || ""} ${savedProfile.last_name || ""}`.trim();
      setFormData({
        name: fullName,
        email: user?.email || "",
        phone: savedProfile.phone || "",
        house: savedProfile.house || "",
        street: savedProfile.street || savedProfile.address || "",
        landmark: savedProfile.landmark || "",
        city: savedProfile.city || "",
        state: savedProfile.state || "",
        pincode: savedProfile.pincode || "",
        addressType: "Home",
      });
      setErrors({});
      setAddressConfirmed(false);
    } else {
      setFormData({
        name: "",
        email: user?.email || "",
        phone: "",
        house: "",
        street: "",
        landmark: "",
        city: "",
        state: "",
        pincode: "",
        addressType: "Home",
      });
      setAddressConfirmed(false);
    }
  };

  // ─── Cart weight calculation (always in KG) ───
  const cartWeight = useMemo(() => {
    const raw = cart.reduce((acc, it) => acc + (Number(it.weight) || 0.3) * (it.qty || 1), 0);
    return Math.max(0.5, raw);
  }, [cart]);

  const totals = useMemo(() => {
    const itemsCount = cart.reduce((sum, it) => sum + (it.qty || 1), 0);
    const subtotal = cart.reduce((sum, it) => sum + (it.price || 0) * (it.qty || 1), 0);
    let shipping = 79;
    if (subtotal < 199) shipping = 49;
    else if (subtotal < 499) shipping = 59;
    const amount = subtotal + shipping;
    return { itemsCount, subtotal, shipping, amount };
  }, [cart]);

  useEffect(() => {
    if (!paying && cart.length === 0 && !orderSuccess) {
      navigate("/cart", { replace: true });
    }
  }, [paying, cart.length, orderSuccess, navigate]);

  if (!paying && cart.length === 0 && !orderSuccess) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    // Auto-lookup when pincode reaches 6 digits
    if (name === "pincode" && /^\d{6}$/.test(value)) {
      lookupPincode(value);
    }
    if (name === "pincode" && value.length < 6) {
      setPincodeStatus("");
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Full name is required";
    if (!formData.email?.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.phone?.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, "")))
      newErrors.phone = "Phone must be 10 digits";
    if (!formData.house?.trim()) newErrors.house = "House / Flat no. is required";
    if (!formData.street?.trim()) newErrors.street = "Street / Area is required";
    if (!formData.landmark?.trim()) newErrors.landmark = "Landmark is required";
    if (!formData.pincode?.trim()) newErrors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Pincode must be 6 digits";
    if (!formData.city?.trim()) newErrors.city = "City is required";
    if (!formData.state?.trim()) newErrors.state = "State is required";
    if (!addressConfirmed) newErrors.confirm = "Please confirm your address is correct";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── MAIN ORDER FLOW ───
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (shippingError) {
      setPaymentError("Delivery not available to this pincode. Please use a different address.");
      return;
    }
    setPaymentError("");
    setPaying(true);

    const fullAddress = composeAddress(formData);

    try {
      const { order, key_id } = await createRazorpayOrder({
        amount: totals.amount,
        receipt: `order_${Date.now()}`,
        notes: {
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
        },
      });

      const paymentResponse = await openRazorpayCheckout({
        order,
        key_id,
        customerInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        onDismiss: () => setPaying(false),
      });

      const verification = await verifyRazorpayPayment({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      });

      if (!verification.success) {
        throw new Error("Payment verification failed");
      }

      const cartItems = cart.map((it) => ({
        id: it.id,
        name: it.name,
        price: it.price,
        qty: it.qty || 1,
        img: it.img,
      }));

      const orderData = {
        user_id: user?.id || null,
        total_amount: totals.amount,
        shipping_fee: totals.shipping,
        status: "processing",
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        shipping_address: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          house: formData.house,
          street: formData.street,
          landmark: formData.landmark,
          address: fullAddress,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          address_type: formData.addressType,
        },
        items: cartItems,
      };

      let savedOrder = null;

      const { data: d1, error: e1 } = await supabase
        .from("orders")
        .insert([orderData])
        .select()
        .single();

      if (e1) {
        console.error("Order save attempt 1 error:", e1.message, e1.details, e1.hint);
        const minimalOrder = {
          user_id: user?.id || null,
          total_amount: totals.amount,
          status: "processing",
        };
        const { data: d2, error: e2 } = await supabase
          .from("orders")
          .insert([minimalOrder])
          .select()
          .single();

        if (e2) {
          console.error("Order save attempt 2 error:", e2.message, e2.details, e2.hint);
        } else {
          savedOrder = d2;
          if (savedOrder?.id) {
            await supabase.from("orders").update({
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              shipping_address: orderData.shipping_address,
              items: cartItems,
            }).eq("id", savedOrder.id).then(({ error: ue }) => {
              if (ue) console.warn("Order update with details failed:", ue.message);
            });
          }
        }
      } else {
        savedOrder = d1;
      }

      const supabaseOrderId = savedOrder?.id || `ORD-${Date.now()}`;

      // Shiprocket — now with structured address fields
      let shiprocketResult = null;
      try {
        shiprocketResult = await createShiprocketOrder({
          order_id: supabaseOrderId,
          order_date:
            new Date().toISOString().split("T")[0] +
            " " +
            new Date().toTimeString().split(" ")[0],
          billing_customer_name: formData.name.split(" ")[0],
          billing_last_name: formData.name.split(" ").slice(1).join(" ") || "",
          billing_address: `${formData.house}, ${formData.street}`,
          billing_address_2: formData.landmark || "",
          billing_city: formData.city,
          billing_pincode: formData.pincode,
          billing_state: formData.state,
          billing_country: "India",
          billing_email: formData.email,
          billing_phone: formData.phone,
          shipping_is_billing: true,
          order_items: cart.map((it) => ({
            name: it.name,
            sku: `SKU-${it.id}`,
            units: it.qty || 1,
            selling_price: String(it.price),
            discount: "0",
            tax: "0",
          })),
          payment_method: "Prepaid",
          sub_total: totals.subtotal,
          length: 15,
          breadth: 12,
          height: 6,
          weight: cartWeight,
        });

        if (shiprocketResult?.success && savedOrder?.id) {
          await supabase
            .from("orders")
            .update({
              shiprocket_order_id: shiprocketResult.shiprocket_order_id,
              shipment_id: shiprocketResult.shipment_id,
              status: "processing",
            })
            .eq("id", savedOrder.id)
            .then(({ error }) => { if (error) console.warn("Supabase order update failed:", error.message); });
        }
      } catch (shipErr) {
        console.warn("Shiprocket order creation failed (non-blocking):", shipErr);
      }

      // Email Confirmation
      const backendUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : (process.env.REACT_APP_BACKEND_URL || "https://ayrine-backend-r8y7.onrender.com");
      fetch(`${backendUrl}/api/email/order-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderData: {
            orderId: supabaseOrderId,
            amount: totals.amount,
            paymentId: paymentResponse.razorpay_payment_id,
            customer: orderData.shipping_address,
            items: cartItems,
            date: new Date().toISOString()
          }
        })
      }).catch(err => console.warn("Email dispatch failed:", err));

      await clearCart();

      setOrderSuccess({
        orderId: supabaseOrderId,
        paymentId: paymentResponse.razorpay_payment_id,
        amount: totals.amount,
        shippingFee: totals.shipping,
        shiprocket: shiprocketResult,
      });
    } catch (err) {
      console.error("Checkout error:", err);
      if (err.message !== "Payment cancelled by user") {
        setPaymentError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setPaying(false);
    }
  };

  // ─── SUCCESS SCREEN ───
  if (orderSuccess) {
    return (
      <div className="checkoutPage">
        <div className="checkoutWrap">
          <div className="orderSuccessBox">
            <div className="successIcon">✓</div>
            <h2 className="successTitle">Order Placed Successfully!</h2>
            <p className="successSub">
              Thank you for shopping with Ayrine. Your order has been confirmed.
            </p>
            <div className="successDetails">
              <div className="successRow">
                <span>Order ID</span>
                <strong>{String(orderSuccess.orderId).substring(0, 12)}</strong>
              </div>
              <div className="successRow">
                <span>Payment ID</span>
                <strong>{orderSuccess.paymentId}</strong>
              </div>
              <div className="successRow">
                <span>Amount Paid</span>
                <strong>₹ {formatINR(orderSuccess.amount)}</strong>
              </div>
              {orderSuccess.shiprocket?.success && orderSuccess.shiprocket?.awb_code && (
                <div className="successRow">
                  <span>Tracking AWB</span>
                  <strong>{orderSuccess.shiprocket.awb_code} ({orderSuccess.shiprocket.courier_name || 'Assigned'})</strong>
                </div>
              )}
              {orderSuccess.shiprocket?.success && !orderSuccess.shiprocket?.awb_code && (
                <div className="successRow">
                  <span>Shipment ID</span>
                  <strong>{orderSuccess.shiprocket.shipment_id || "Processing"}</strong>
                </div>
              )}
            </div>
            <div className="successActions">
              <Link to="/orders" className="successBtn primary">
                View My Orders
              </Link>
              <Link to="/" className="successBtn ghost">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── CHECKOUT FORM ───
  return (
    <div className="checkoutPage">
      <div className="checkoutWrap">
        <h2 className="checkoutTitle">Checkout</h2>

        {paymentError && (
          <div className="checkoutError" role="alert">
            {paymentError}
          </div>
        )}

        <form className="checkoutForm" onSubmit={handleSubmit}>
          <div className="checkoutLayout">
            {/* Shipping Address */}
            <section className="checkoutSection">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 className="checkoutSectionTitle" style={{ margin: 0 }}>Shipping Address</h3>
                {savedProfile && (
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", color: "var(--maroon)" }}>
                    <input
                      type="checkbox"
                      checked={useSavedAddress}
                      onChange={handleToggleAddress}
                    />
                    Use Saved Address
                  </label>
                )}
              </div>
              <div className="checkoutFields">
                {/* ── Contact Info ── */}
                <div className="fieldGroup">
                  <label htmlFor="name">Full Name *</label>
                  <input id="name" name="name" type="text" placeholder="Enter your full name" value={formData.name} onChange={handleChange} className={errors.name ? "error" : ""} disabled={paying} />
                  {errors.name && <span className="fieldError">{errors.name}</span>}
                </div>

                <div className="fieldGroup">
                  <label htmlFor="phone">Phone Number *</label>
                  <input id="phone" name="phone" type="tel" placeholder="10-digit mobile number" value={formData.phone} onChange={handleChange} className={errors.phone ? "error" : ""} disabled={paying} />
                  {errors.phone && <span className="fieldError">{errors.phone}</span>}
                </div>

                <div className="fieldGroup fullWidth">
                  <label htmlFor="email">Email *</label>
                  <input id="email" name="email" type="email" placeholder="your@email.com" value={formData.email} onChange={handleChange} className={errors.email ? "error" : ""} disabled={paying} />
                  {errors.email && <span className="fieldError">{errors.email}</span>}
                </div>

                {/* ── Separator ── */}
                <div className="fieldSeparator" />

                {/* ── Address Fields ── */}
                <div className="fieldGroup fullWidth">
                  <label htmlFor="house">House / Flat No *</label>
                  <input id="house" name="house" type="text" placeholder="e.g. 12-3-456, Flat 201" value={formData.house} onChange={handleChange} className={errors.house ? "error" : ""} disabled={paying} />
                  {errors.house && <span className="fieldError">{errors.house}</span>}
                </div>

                <div className="fieldGroup fullWidth">
                  <label htmlFor="street">Street / Area *</label>
                  <input id="street" name="street" type="text" placeholder="e.g. MG Road, Banjara Hills" value={formData.street} onChange={handleChange} className={errors.street ? "error" : ""} disabled={paying} />
                  {errors.street && <span className="fieldError">{errors.street}</span>}
                </div>

                <div className="fieldGroup fullWidth">
                  <label htmlFor="landmark">Landmark *</label>
                  <input id="landmark" name="landmark" type="text" placeholder="e.g. Near City Center Mall" value={formData.landmark} onChange={handleChange} className={errors.landmark ? "error" : ""} disabled={paying} />
                  {errors.landmark && <span className="fieldError">{errors.landmark}</span>}
                </div>

                {/* ── Separator ── */}
                <div className="fieldSeparator" />

                {/* ── Pincode → City / State ── */}
                <div className="fieldGroup">
                  <label htmlFor="pincode">
                    Pincode *
                    {pincodeLoading && <span className="pincodeSpin"> ↻</span>}
                    {pincodeStatus === "success" && <span className="pincodeOk"> ✓</span>}
                    {pincodeStatus === "error" && <span className="pincodeFail"> ✗ Invalid</span>}
                  </label>
                  <input id="pincode" name="pincode" type="text" placeholder="6-digit pincode" maxLength={6} value={formData.pincode} onChange={handleChange} className={errors.pincode ? "error" : ""} disabled={paying} />
                  {errors.pincode && <span className="fieldError">{errors.pincode}</span>}
                </div>

                <div className="fieldGroup">
                  <label htmlFor="city">District * {pincodeStatus === "success" && <span className="autoFillTag">auto-filled</span>}</label>
                  <input id="city" name="city" type="text" placeholder="City / District" value={formData.city} onChange={handleChange} className={errors.city ? "error" : ""} disabled={paying} />
                  {errors.city && <span className="fieldError">{errors.city}</span>}
                </div>

                <div className="fieldGroup fullWidth">
                  <label htmlFor="state">State * {pincodeStatus === "success" && <span className="autoFillTag">auto-filled</span>}</label>
                  <input id="state" name="state" type="text" placeholder="State" value={formData.state} onChange={handleChange} className={errors.state ? "error" : ""} disabled={paying} />
                  {errors.state && <span className="fieldError">{errors.state}</span>}
                </div>

                {/* ── Separator ── */}
                <div className="fieldSeparator" />

                {/* ── Address Type ── */}
                <div className="fieldGroup fullWidth">
                  <label>Address Type</label>
                  <div className="addressTypeRow">
                    <label className={`addressTypeOption ${formData.addressType === "Home" ? "active" : ""}`}>
                      <input type="radio" name="addressType" value="Home" checked={formData.addressType === "Home"} onChange={handleChange} disabled={paying} />
                      <span className="radioIcon">🏠</span> Home
                    </label>
                    <label className={`addressTypeOption ${formData.addressType === "Work" ? "active" : ""}`}>
                      <input type="radio" name="addressType" value="Work" checked={formData.addressType === "Work"} onChange={handleChange} disabled={paying} />
                      <span className="radioIcon">🏢</span> Work
                    </label>
                  </div>
                </div>

                {/* ── Separator ── */}
                <div className="fieldSeparator" />

                {/* ── Address Confirmation ── */}
                <div className="fieldGroup fullWidth">
                  <label className={`addressConfirmLabel ${errors.confirm ? "confirmError" : ""}`}>
                    <input type="checkbox" checked={addressConfirmed} onChange={(e) => { setAddressConfirmed(e.target.checked); if (e.target.checked) setErrors((p) => ({ ...p, confirm: "" })); }} disabled={paying} />
                    <span>I confirm that this address is complete and correct</span>
                  </label>
                  {errors.confirm && <span className="fieldError">{errors.confirm}</span>}
                </div>
              </div>
            </section>

            {/* Order Summary */}
            <section className="checkoutSection checkoutOrder">
              <h3 className="checkoutSectionTitle">Order Summary</h3>
              <div className="checkoutItems">
                {cart.map((it) => (
                  <div className="checkoutItem" key={it.id}>
                    <div className="checkoutItemImg">
                      <img src={it.img} alt={it.name} />
                    </div>
                    <div className="checkoutItemInfo">
                      <Link to={it.link || "/"} className="checkoutItemName">
                        {it.name}
                      </Link>
                      <div className="checkoutItemMeta">
                        <span>Qty: {it.qty || 1}</span>
                        <span>₹ {formatINR((it.price || 0) * (it.qty || 1))}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="checkoutTotals">
                <div className="checkoutTotalsRow">
                  <span>Subtotal ({totals.itemsCount} {totals.itemsCount === 1 ? 'item' : 'items'})</span>
                  <span>₹ {formatINR(totals.subtotal)}</span>
                </div>
                <div className="checkoutTotalsRow">
                  <span>Shipping</span>
                  <span style={{ fontWeight: '500' }}>
                    ₹ {formatINR(totals.shipping)}
                  </span>
                </div>
                <div className="checkoutTotalsRow total">
                  <span>Total</span>
                  <span>₹ {formatINR(totals.amount)}</span>
                </div>
              </div>
              <button
                type="submit"
                className="placeOrderBtn"
                disabled={paying || !!shippingError}
              >
                {paying ? "Processing..." : `Pay ₹ ${formatINR(totals.amount)}`}
              </button>
              <div className="paymentSecure">
                🔒 Secured by Razorpay
              </div>
              <Link to="/cart" className="backToCart">
                ← Back to Cart
              </Link>
            </section>
          </div>
        </form>
      </div>
    </div>
  );
}
